import { memo } from 'react';
import PropTypes from 'prop-types';
import { CatalogWidget, WidgetSkeleton } from '@aimsos/ui';

import { useWidgetData } from '../../widgets/hooks/useWidgetData.js';

/**
 * One placed widget's body on the dashboard canvas (CT-5183).
 *
 * The canvas draws through `CatalogWidget`, the same renderer the library's
 * detail preview and the builder's live preview use, so a widget looks the same
 * everywhere it appears. This adds the other half of that promise: it also
 * shows the same **data**, by running the placement's catalog widget through
 * `widget/execute`.
 *
 * While the execution is in flight the body is a skeleton, not the sample:
 * drawing plausible numbers and swapping them for different ones a moment
 * later is the one outcome worse than waiting, because nothing on screen says
 * the first set was never real.
 *
 * `widgetUuid` is null for the seeded widgets the canvas still offers — they
 * point at no stored definition, so there is nothing to execute, the hook
 * stays idle and there is nothing to wait for. `data` is then null and
 * `CatalogWidget` draws its sample, which is also what happens for a type with
 * no honest reading of a result. Both are the right answer: a card that
 * renders nothing reads as broken.
 *
 * `memo()` because the canvas re-renders on every drag and resize, and each of
 * these owns a request.
 */
const CanvasWidget = memo(function CanvasWidget({ type, widgetUuid, compact }) {
  const { data, hasNoData, isLoading } = useWidgetData({ widgetUuid });

  if (isLoading) return <WidgetSkeleton className="min-h-[120px]" />;

  // `useSampleData={false}` once the widget has run and come back with
  // nothing to draw — empty, or failed. A slot that answers either with the
  // catalogue's sample is lying.
  return (
    <CatalogWidget
      type={type}
      data={data ?? undefined}
      useSampleData={!hasNoData}
      compact={compact}
    />
  );
});

CanvasWidget.propTypes = {
  type: PropTypes.string.isRequired,
  /** The catalog widget this placement pins; null for a seeded widget. */
  widgetUuid: PropTypes.string,
  compact: PropTypes.bool,
};

CanvasWidget.defaultProps = {
  widgetUuid: null,
  compact: false,
};

export default CanvasWidget;
